import {
  resolveFlowBindings,
  stableFlowJson,
  type BindingOrigin,
  type FlowExpressionContext,
  type ResolvedNodeBindings,
} from "../contract/expression.js";
import { isAgentNode, isSubpromptNode, nodeBindings } from "./node-shape.js";
import type { FlowDefinition, FlowNode, NodeExecutionSnapshot } from "./types.js";

/** Build the bounded expression context from immutable definition and snapshot state. */
export function buildFlowExpressionContext(
  flow: FlowDefinition,
  snapshots: Map<string, NodeExecutionSnapshot>,
): FlowExpressionContext {
  const nodes: FlowExpressionContext["nodes"] = {};
  for (const node of flow.nodes) {
    const snapshot = snapshots.get(node.id);
    const output =
      snapshot?.status === "complete" && snapshot.output?.fields
        ? { fields: snapshot.output.fields }
        : undefined;
    nodes[node.id] = { status: snapshot?.status ?? "not_started", output };
  }
  return {
    flow: { input: flow.defaults?.run_input ?? {} },
    defaults: flow.defaults,
    nodes,
  };
}

/**
 * Classify what one producer node's output counts as for a downstream binding.
 *
 * Transitive by induction, in a single lookup: a producer's persisted
 * `inputOrigins` were themselves classified under this rule when it ran, so
 * they already carry any taint from its own ancestors — no graph walk. Absent
 * origins read as `"agent"` (fail-closed): a snapshot persisted before the
 * field existed cannot prove the node's inputs were clean, and this label is
 * one a human approver acts on. `{}` — a node that bound nothing — is
 * `"runtime"`.
 */
export function producerOutputObtainedBy(
  producer: FlowNode,
  snapshot: NodeExecutionSnapshot | undefined,
): "agent" | "runtime" {
  // A subprompt's output is model-generated the same as an agent's, even though
  // the runtime (not the session agent) makes the call — so a downstream check
  // must derive "asserted", never "verified", over it. See engine/check-provenance.ts.
  if (isAgentNode(producer) || isSubpromptNode(producer)) return "agent";
  const inputOrigins = snapshot?.inputOrigins;
  if (!inputOrigins) return "agent";
  const tainted = Object.values(inputOrigins).some((origins) =>
    // A hydrated snapshot is host-supplied and only shape-checked as JSON, so a
    // malformed entry is unreadable rather than clean — fail closed, never throw.
    Array.isArray(origins) ? origins.some((origin) => origin?.obtainedBy === "agent") : true,
  );
  return tainted ? "agent" : "runtime";
}

/** Resolve one node's named inputs and derive their runtime/agent origins. */
export function resolveNodeBindings(
  flow: FlowDefinition,
  node: FlowNode,
  snapshots: Map<string, NodeExecutionSnapshot>,
  flowInputOrigins: Record<string, BindingOrigin[]> = {},
): ResolvedNodeBindings {
  const origins = Object.fromEntries(
    flow.nodes.map((producer) => [
      producer.id,
      producerOutputObtainedBy(producer, snapshots.get(producer.id)),
    ]),
  ) as Record<string, "agent" | "runtime">;
  return resolveFlowBindings(
    nodeBindings(node),
    buildFlowExpressionContext(flow, snapshots),
    origins,
    flowInputOrigins,
  );
}

/** Interpolate named, already-resolved binding values into an agent instruction. */
export function interpolateNodeInstruction(
  instruction: string,
  values: Record<string, unknown>,
): string {
  return instruction.replace(/\$\{([A-Za-z_][A-Za-z0-9_-]*)\}/g, (_placeholder, name: string) => {
    if (!Object.hasOwn(values, name)) {
      throw new Error(`Node instruction binding ${name} is missing`);
    }
    const value = values[name];
    return typeof value === "string" ? value : stableFlowJson(value);
  });
}
